import { Star1StreamlineCore, OfficeWorkerStreamlineCore } from '@upright/ui/icons';
import BagSuitcaseIcon from '@upright/ui/icons/bag-suitcase-1-streamline-core';
import BuildingOfficeIcon from '@upright/ui/icons/building-office-streamline-core';
import SearchDollarIcon from '@upright/ui/icons/search-dollar-streamline-core';
import ZoomDocumentIcon from '@upright/ui/icons/zoom-document-streamline-core';
import {
  CUSTOM_COMPANIES_PATH,
  FAVORITES_PATH,
  FUNDS_AND_PORTFOLIOS_PATH,
  MANAGE_ACCESS_PATH,
  SCREEN_COMPANIES_PATH,
  SCREEN_FUNDS_PATH,
} from '@upright/ui/common/generic/components/navigation/page-paths';
import { useLocation } from 'react-router-dom';
import { useFrontPageConfig, showSharedAssets } from '../front-page-config';
import {
  StyledNavList,
  TopLevelItemWrap,
  SubNavItem,
  getAriaCurrent,
} from '@upright/ui/views/shared/navigation/styled-nav-components';
import styled from 'styled-components';
import { useIsTeam } from '@upright/ui/common/generic/hooks';
import { getDefaultOrganization } from '@upright/common/iam/organizations/common';
import { useMyOrganizationsQuery } from '@upright/ui/api/iam/organizations/hooks';
import { useOrganizationHasCreateCompaniesPermission } from '../custom-companies-view/hooks/use-organization-has-create-companies-permission';
import { OrganizationSelectorSidebar } from './organization-selector-sidebar';

export default function MainNavigation() {
  const location = useLocation();
  const { clientType } = useFrontPageConfig();
  const isTeam = useIsTeam();
  const organizationsQuery = useMyOrganizationsQuery();
  const selectedOrganization = getDefaultOrganization(organizationsQuery.data?.organizations, isTeam);
  const { hasPermission: orgHasCreateCompaniesPermission } = useOrganizationHasCreateCompaniesPermission(
    selectedOrganization?.id,
  );
  const canSeeCustomCompanies = isTeam || orgHasCreateCompaniesPermission;

  const navigationConfig = {
    savedItems: [{ to: FAVORITES_PATH, label: 'Favorites', icon: Star1StreamlineCore }],
  };

  const assetsConfig = [
    // Main portfolios/funds entry
    {
      to: '/',
      label: 'Funds and portfolios',
      icon: BagSuitcaseIcon as React.ComponentType<{ width?: number; height?: number }>,
      // Special handling for root path: must match exactly '/' or the assets table's own route
      // Cannot use getAriaCurrent because includes('/') would match all paths
      ariaCurrent:
        location.pathname === '/' || location.pathname === FUNDS_AND_PORTFOLIOS_PATH ? ('page' as const) : undefined,
    },
    // Custom companies: always visible; disabled + tooltip when user/org cannot create companies
    {
      to: CUSTOM_COMPANIES_PATH,
      label: 'Custom companies',
      icon: BuildingOfficeIcon as React.ComponentType<{ width?: number; height?: number }>,
      ariaCurrent: getAriaCurrent(CUSTOM_COMPANIES_PATH, location.pathname),
      disabled: !canSeeCustomCompanies,
      tooltipText: !canSeeCustomCompanies
        ? 'Analyze any private or public company not in Upright’s database by entering the company URL and basic information. Contact your Upright customer representative to unlock access.'
        : undefined,
    },
    ...(showSharedAssets(clientType)
      ? [
          {
            to: MANAGE_ACCESS_PATH,
            label: 'Manage access',
            icon: OfficeWorkerStreamlineCore as React.ComponentType<{ width?: number; height?: number }>,
            ariaCurrent: getAriaCurrent(MANAGE_ACCESS_PATH, location.pathname),
          },
        ]
      : []),
  ];

  return (
    <StyledNavList>
      <OrgSelectorWrap>
        <OrganizationSelectorSidebar />
      </OrgSelectorWrap>

      {/* Assets & access */}
      <TopLevelItemWrap $isFirstItem>
        {assetsConfig.map(item => (
          <SubNavItem
            key={item.to}
            to={item.to}
            ariaCurrent={item.ariaCurrent}
            icon={item.icon}
            // Optional props supported by SubNavItem: only set when present
            disabled={(item as any).disabled}
            tooltipText={(item as any).tooltipText}
          >
            {item.label}
          </SubNavItem>
        ))}
      </TopLevelItemWrap>

      {/* Separator */}
      <TopLevelItemWrap $isSection>
        <DividerLine />
      </TopLevelItemWrap>

      {/* Tools / screeners */}
      <TopLevelItemWrap $isSection>
        <SubNavItem
          to={SCREEN_COMPANIES_PATH}
          ariaCurrent={getAriaCurrent(SCREEN_COMPANIES_PATH, location.pathname)}
          icon={ZoomDocumentIcon as React.ComponentType<{ width?: number; height?: number }>}
        >
          Screen companies
        </SubNavItem>
        <SubNavItem
          to={SCREEN_FUNDS_PATH}
          ariaCurrent={getAriaCurrent(SCREEN_FUNDS_PATH, location.pathname)}
          icon={SearchDollarIcon as React.ComponentType<{ width?: number; height?: number }>}
        >
          Screen funds
        </SubNavItem>
      </TopLevelItemWrap>

      {/* Separator */}
      <TopLevelItemWrap $isSection>
        <DividerLine />
      </TopLevelItemWrap>

      {/* Saved items (Favorites etc.) */}
      <TopLevelItemWrap $isSection>
        {navigationConfig.savedItems.map(item => (
          <SubNavItem
            key={item.to}
            to={item.to}
            ariaCurrent={getAriaCurrent(item.to, location.pathname)}
            icon={item.icon}
          >
            {item.label}
          </SubNavItem>
        ))}
      </TopLevelItemWrap>
    </StyledNavList>
  );
}

const DividerLine = styled.hr`
  border: none;
  border-top: 1px solid var(--borderColor-muted, #d0d7de);
  margin: var(--space__lg) var(--space__sm);
`;

const OrgSelectorWrap = styled.div`
  margin-bottom: var(--space__lg);
`;
